import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/components/ui/button';
import { 
  Brain, 
  Users, 
  Target, 
  Sparkles, 
  Globe, 
  Check,
  ArrowRight,
  Trophy,
  Zap,
  Heart,
  Dumbbell,
  Activity,
  Moon,
  Smile,
  Apple,
  Mic,
  BookOpen,
  Lightbulb,
  CheckCircle2,
  TrendingUp,
  Repeat,
  DollarSign,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Play,
  Leaf,
  Compass,
  Coins,
  Mountain,
  Crown,
  Landmark
} from 'lucide-react';
import { bookExamples, coreAreas } from '../data/bookExamples';
import { getShowcaseFor } from '../data/mycraftzShowcase';
// WaitlistMultiStep removed from the hero — data capture migrates to /signup.
import MasterHomepage from '../components/MasterHomepage';
import {
  PathOfTransformation,
  PopularCollections,
  TransformationRibbon,
  ClosingOmRibbon,
} from '../components/MasterHomepageSections';
import BookWhispersSection from '../components/BookWhispersSection';
import ChaptersWithinSection from '../components/ChaptersWithinSection';
import LandingFooter from '../components/landing/LandingFooter';
import CraftYourReadingJourney from '../components/CraftYourReadingJourney';
import MyCraftzJourneyTeaser from '../components/MyCraftzJourneyTeaser';
// TransformationJourneySection merged into MyCraftzJourneyTeaser per ICRAFTZ 2.0 IA.
import MyCraftzInstallOverlay from '../components/MyCraftzInstallOverlay';
import LandingNav from '../components/landing/LandingNav';
import UserMenu from '../components/UserMenu';
import translations from '../i18n/translations';
import staticBookTranslations from '../i18n/book_translations.json';
import '../styles/LandingPage.css';

// Component to remove white background from logo
const TransparentLogo = ({ src, alt, className }) => {
  const canvasRef = useRef(null);
  const [processedSrc, setProcessedSrc] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Make white/near-white pixels transparent
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        // Check if pixel is white or near-white
        if (r > 240 && g > 240 && b > 240) {
          data[i + 3] = 0; // Make transparent
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      setProcessedSrc(canvas.toDataURL('image/png'));
    };
    img.src = src;
  }, [src]);

  if (!processedSrc) {
    return <div className={className} style={{ background: 'transparent' }} />;
  }

  return <img src={processedSrc} alt={alt} className={className} />;
};

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const iconMap = {
  Trophy, Zap, Heart, Dumbbell, Activity, Moon, Smile, Apple, Mic, Repeat, DollarSign, Lightbulb,
  Leaf, Compass, Coins, Mountain, Crown, Landmark, BookOpen, Target
};

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी (Hi)' },
  { code: 'te', label: 'తెలుగు (Te)' },
  { code: 'ta', label: 'தமிழ் (Ta)' },
  { code: 'kn', label: 'ಕನ್ನಡ (Kn)' },
  { code: 'mr', label: 'मराठी (Mr)' },
  { code: 'bn', label: 'বাংলা (Bn)' },
  { code: 'gu', label: 'ગુજરાતી (Gu)' },
  { code: 'ml', label: 'മലയാളം (Ml)' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ (Pa)' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState('');
  const [waitlistMessage, setWaitlistMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [installBook, setInstallBook] = useState(null); // MyCraftz Install Overlay target
  const [showLibraryBanner, setShowLibraryBanner] = useState(false); // surfaced when MyCraftz nav clicked
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Language state
  const [activeLang, setActiveLang] = useState('en');
  const t = translations[activeLang] || translations.en;
  
  // Translation state for book content
  const translationCache = useRef({});
  const [translatedContent, setTranslatedContent] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  // Curated Collections — resource picker (audio / infographic)
  // Shape: { pairingIndex: number, type: 'audio' | 'infographic' } | null
  const [resourcePicker, setResourcePicker] = useState(null);

  // In-app audio player state
  // Shape: { pairingIndex, langCode, langLabel } | null
  const [audioPlayer, setAudioPlayer] = useState(null);

  // In-app infographic viewer state
  // Shape: { pairingIndex, langCode, langLabel } | null
  const [infographicViewer, setInfographicViewer] = useState(null);

  // In-app book preview video player state
  // Shape: { title, author, videoSrc } | null
  const [videoPreview, setVideoPreview] = useState(null);

  // "How It Works" — progressive reveal of the demo book picker
  const [showDemoPicker, setShowDemoPicker] = useState(false);

  // Book Modal — Notify Me (Vernacular Coming Soon) form state
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyLang, setNotifyLang] = useState('en');
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyStatus, setNotifyStatus] = useState(''); // '', 'success', 'error'
  const [notifyMessage, setNotifyMessage] = useState('');

  const NOTIFY_LANGS = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'हिंदी (Hi)' },
    { code: 'te', label: 'తెలుగు (Te)' },
    { code: 'ta', label: 'தமிழ் (Ta)' },
    { code: 'kn', label: 'ಕನ್ನಡ (Kn)' },
    { code: 'mr', label: 'मराठी (Mr)' },
    { code: 'bn', label: 'বাংলা (Bn)' },
    { code: 'gu', label: 'ગુજરાતી (Gu)' },
    { code: 'ml', label: 'മലയാളം (Ml)' },
    { code: 'pa', label: 'ਪੰਜਾਬੀ (Pa)' },
  ];

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!selectedBook || !notifyEmail.trim() || notifySubmitting) return;
    setNotifySubmitting(true);
    setNotifyStatus('');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_URL}/api/book-purchase-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: notifyEmail.trim(),
          book_title: selectedBook.title,
          language: notifyLang,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setNotifyStatus('success');
        setNotifyMessage(data.message || "You're on the list! We'll notify you the moment this book is available on iCraftz.");
        setNotifyEmail('');
      } else {
        setNotifyStatus('error');
        setNotifyMessage(data.detail || 'Could not save your interest. Please try again.');
      }
    } catch (err) {
      setNotifyStatus('error');
      setNotifyMessage('Could not save your interest. Please try again.');
    } finally {
      setNotifySubmitting(false);
    }
  };

  // Reset Notify Me form whenever the book modal closes or a different book opens
  useEffect(() => {
    setNotifyOpen(false);
    setNotifyEmail('');
    setNotifyLang(activeLang || 'en');
    setNotifyStatus('');
    setNotifyMessage('');
    setNotifySubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook]);

  // Auto-open a book modal when navigated with ?book=<id> (used by /blogs related books)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const bookId = params.get('book');
      if (bookId) {
        const found = bookExamples.find((b) => String(b.id) === String(bookId));
        if (found) {
          setSelectedBook(found);
          // scroll library into view; modal sits over it
          setTimeout(() => {
            const lib = document.getElementById('library');
            if (lib) lib.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
          // strip the ?book=... so back/refresh doesn't keep re-opening
          const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    } catch (err) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map of books that have an AI-generated preview video (demo)
  const bookVideoPreviews = {
    'Atomic Habits': '/videos/atomic_habits_preview.mp4',
    'The Compound Effect': '/videos/compound_effect_preview.mp4',
  };

  // Resource links per pairing.
  // Pairing 1: audio + infographic both wired.
  // Pairings 2-5: infographic wired (en/hi/gu/mr/kn). Audio coming soon.
  const pairingResources = {
    1: {
      title: 'Habits + Compounding',
      covers: [
        'https://covers.openlibrary.org/b/isbn/9780735211292-L.jpg',
        'https://books.google.com/books/content?id=P28DsLkv5cgC&printsec=frontcover&img=1&zoom=1&source=gbs_api',
      ],
      bookNames: ['Atomic Habits', 'The Compound Effect'],
      audio: {
        en: '/audio/pairing1/audio_en.m4a',
        hi: '/audio/pairing1/audio_hi.m4a',
        gu: '/audio/pairing1/audio_gu.m4a',
        mr: '/audio/pairing1/audio_mr.m4a',
        kn: '/audio/pairing1/audio_kn.m4a',
      },
      infographic: {
        en: '/collections/pairing1/infographic_en.webp',
        hi: '/collections/pairing1/infographic_hi.webp',
        gu: '/collections/pairing1/infographic_gu.webp',
        mr: '/collections/pairing1/infographic_mr.webp',
        kn: '/collections/pairing1/infographic_kn.webp',
      },
    },
    2: {
      title: 'Purpose + Wisdom',
      bookNames: ['Ikigai', "Life's Amazing Secrets"],
      infographic: {
        en: '/collections/pairing2/infographic_en.webp',
        hi: '/collections/pairing2/infographic_hi.webp',
        gu: '/collections/pairing2/infographic_gu.webp',
        mr: '/collections/pairing2/infographic_mr.webp',
        kn: '/collections/pairing2/infographic_kn.webp',
      },
    },
    3: {
      title: 'Mind + Money',
      bookNames: ['Think and Grow Rich', 'Rich Dad Poor Dad'],
      infographic: {
        en: '/collections/pairing3/infographic_en.webp',
        hi: '/collections/pairing3/infographic_hi.webp',
        gu: '/collections/pairing3/infographic_gu.webp',
        mr: '/collections/pairing3/infographic_mr.webp',
        kn: '/collections/pairing3/infographic_kn.webp',
      },
    },
    4: {
      title: 'Focus + Execution',
      bookNames: ['The ONE Thing', 'Deep Work'],
      infographic: {
        en: '/collections/pairing4/infographic_en.webp',
        hi: '/collections/pairing4/infographic_hi.webp',
        gu: '/collections/pairing4/infographic_gu.webp',
        mr: '/collections/pairing4/infographic_mr.webp',
        kn: '/collections/pairing4/infographic_kn.webp',
      },
    },
    5: {
      title: 'Mindset + Action',
      bookNames: ['Attitude is Everything', "Build, Don't Talk"],
      infographic: {
        en: '/collections/pairing5/infographic_en.webp',
        hi: '/collections/pairing5/infographic_hi.webp',
        gu: '/collections/pairing5/infographic_gu.webp',
        mr: '/collections/pairing5/infographic_mr.webp',
        kn: '/collections/pairing5/infographic_kn.webp',
      },
    },
  };

  const pickerLanguages = [
    { code: 'en', glyph: 'EN', label: 'English' },
    { code: 'hi', glyph: 'हि', label: 'हिंदी' },
    { code: 'gu', glyph: 'ગુ', label: 'ગુજરાતી' },
    { code: 'mr', glyph: 'म', label: 'मराठी' },
    { code: 'kn', glyph: 'ಕ', label: 'ಕನ್ನಡ' },
  ];

  const openResourcePicker = (pairingIndex, type) => {
    if (!pairingResources[pairingIndex]) return;
    if (!pairingResources[pairingIndex][type]) return;
    setResourcePicker({ pairingIndex, type });
  };

  const getResourceUrl = (langCode) => {
    if (!resourcePicker) return '#';
    const { pairingIndex, type } = resourcePicker;
    return pairingResources[pairingIndex]?.[type]?.[langCode] || '#';
  };

  const handleAudioLangPick = (e, langCode) => {
    // Intercept click for audio picks → open in-app player instead of navigating
    if (!resourcePicker || resourcePicker.type !== 'audio') return; // let infographic anchors navigate
    e.preventDefault();
    const { pairingIndex } = resourcePicker;
    const lang = pickerLanguages.find((l) => l.code === langCode);
    setAudioPlayer({ pairingIndex, langCode, langLabel: lang?.label || langCode });
    setResourcePicker(null);
  };

  const handleInfographicLangPick = (e, langCode) => {
    // Intercept click for infographic picks → open in-app viewer (iOS Safari won't render .webp inline)
    if (!resourcePicker || resourcePicker.type !== 'infographic') return;
    e.preventDefault();
    const { pairingIndex } = resourcePicker;
    const lang = pickerLanguages.find((l) => l.code === langCode);
    setInfographicViewer({ pairingIndex, langCode, langLabel: lang?.label || langCode });
    setResourcePicker(null);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    
    setIsSubmitting(true);
    setWaitlistStatus('');
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setWaitlistStatus('success');
        setWaitlistMessage(data.message);
        setEmail('');
      } else {
        setWaitlistStatus('error');
        setWaitlistMessage(data.message || t.waitlist.error);
      }
    } catch (err) {
      setWaitlistStatus('error');
      setWaitlistMessage(t.waitlist.error);
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setWaitlistStatus('');
        setWaitlistMessage('');
      }, 5000);
    }
  };

  const stats = [
    { number: "500M+", label: t.library?.subheading?.includes('500') ? "संभावित उपयोगकर्ता" : "Potential Users" },
    { number: "15+", label: activeLang === 'en' ? "Languages" : t.nav?.library || "Languages" },
    { number: "1000+", label: activeLang === 'en' ? "Book Summaries" : t.library?.heading || "Book Summaries" }
  ];

  // Get books for selected category
  const getCategoryBooks = (categoryName) => {
    return bookExamples.filter(book => 
      book.categories && book.categories.includes(categoryName)
    );
  };

  const handleCategoryClick = (area) => {
    setSelectedCategory(area);
  };

  // Heading helpers — orange-anchor system applied only on English UI.
  // Maintains consistent brand color story (MORE THOUGHT → START GROWING → Books Reprogram You).
  const accentLastWord = (text) => {
    if (activeLang !== 'en' || !text) return text;
    const parts = text.trim().split(' ');
    if (parts.length < 2) return text;
    const last = parts.pop();
    return (
      <>
        {parts.join(' ')}{' '}
        <span className="hero-tagline-thought">{last}</span>
      </>
    );
  };

  const accentFirstWord = (text) => {
    if (activeLang !== 'en' || !text) return text;
    const parts = text.trim().split(' ');
    if (parts.length < 2) return text;
    const first = parts.shift();
    return (
      <>
        <span className="hero-tagline-thought">{first}</span>{' '}
        {parts.join(' ')}
      </>
    );
  };

  // Translate book content when modal opens or language changes.
  // Strategy: hit the static pre-translation bundle first (instant, zero network).
  // Only fall back to the live API if a translation is missing.
  const translateBookContent = async (book, langCode) => {
    if (langCode === 'en') {
      setTranslatedContent(null);
      setIsTranslating(false);
      return;
    }

    const cacheKey = `${book.title}-${langCode}`;
    if (translationCache.current[cacheKey]) {
      setTranslatedContent(translationCache.current[cacheKey]);
      setIsTranslating(false);
      return;
    }

    // 1) Static pre-translation lookup — synchronous, no lag.
    const staticEntry = staticBookTranslations?.[book.title]?.[langCode];
    if (staticEntry) {
      const data = { ...staticEntry, success: true };
      translationCache.current[cacheKey] = data;
      setTranslatedContent(data);
      setIsTranslating(false);
      return;
    }

    // 2) Fallback: live API (covers any newly added books not yet in the bundle).
    setIsTranslating(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/translate-book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary: book.summary,
          insights: book.insights,
          actionItems: book.actionItems,
          coreTheme: book.coreTheme,
          targetLanguage: langCode,
          bookTitle: book.title
        })
      });

      const data = await response.json();
      
      if (data.success) {
        translationCache.current[cacheKey] = data;
        setTranslatedContent(data);
      } else {
        setTranslatedContent(null);
      }
    } catch (err) {
      setTranslatedContent(null);
    } finally {
      setIsTranslating(false);
    }
  };

  // Trigger translation when book modal opens or language changes
  useEffect(() => {
    if (selectedBook) {
      translateBookContent(selectedBook, activeLang);
    }
  }, [selectedBook, activeLang]);

  // Reset translation when modal closes
  useEffect(() => {
    if (!selectedBook) {
      setTranslatedContent(null);
      setIsTranslating(false);
    }
  }, [selectedBook]);

  // Get translated or original content
  const getBookContent = (book) => {
    if (activeLang === 'en' || !translatedContent) {
      return {
        summary: book.summary,
        insights: book.insights,
        actionItems: book.actionItems,
        coreTheme: book.coreTheme
      };
    }
    return {
      summary: translatedContent.summary || book.summary,
      insights: translatedContent.insights || book.insights,
      actionItems: translatedContent.actionItems || book.actionItems,
      coreTheme: translatedContent.coreTheme || book.coreTheme
    };
  };

  return (
    <div className="landing-page landing-page-master">
      {/* User account / login (top-right) — hidden on master homepage by CSS */}
      <UserMenu />

      {/* ════════════════════════════════════════════════════════════
          iCRAFTZ Homepage — Mockup v6 approved baseline.
          The founder-painted manuscript IS the page. 22 transparent
          percentage-positioned hotspots preserve all existing
          functionality (nav, language switch, CTAs, tablets, cards).
          ════════════════════════════════════════════════════════════ */}
      <MasterHomepage
        activeLang={activeLang}
        onLanguageChange={setActiveLang}
        onNavigate={(path) => navigate(path)}
      />

      {/* ICRAFTZ 2.0 — Homepage IA per user direction:
            Hero → Library → Curated Collections → Reading Journeys → MyCraftz (merged with 5-stage)
         The standalone <TransformationJourneySection /> has been merged into
         <MyCraftzJourneyTeaser /> below since both showed the same 5-step
         Jigyasa→Siddhi chakra. */}

      {/* Book Library - Vintage Bookshelf */}
      <section className="vintage-library-section">
        <div className="container">
          <motion.div
            className="library-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="library-title">{accentLastWord(t.library.heading)}</h2>
            <p className="library-subtitle">
              49 transformative books with AI-powered action plans in your language
            </p>
          </motion.div>

          {showLibraryBanner && (
            <div
              className="library-mycraftz-banner"
              data-testid="library-mycraftz-banner"
              role="status"
            >
              <p className="library-mycraftz-banner-title">
                {(t.libraryBanner && t.libraryBanner.title) || "Every transformation begins with one idea."}
              </p>
              <p className="library-mycraftz-banner-sub">
                {(t.libraryBanner && t.libraryBanner.sub) || "Select a book. Discover an idea. Install a practice."}
              </p>
            </div>
          )}

          <div className="bookshelf">
            <div className="books-grid-large">
              {bookExamples.map((book, index) => (
                <motion.div
                  key={book.id}
                  className={`book-spine${bookVideoPreviews[book.title] ? ' book-spine--preview' : ''}`}
                  data-testid={`book-card-${book.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: Math.min(index * 0.02, 0.5) }}
                  onClick={() => setSelectedBook(book)}
                >
                  {bookVideoPreviews[book.title] && (
                    <span className="book-preview-ribbon">FULL PREVIEW</span>
                  )}
                  <img 
                    src={book.image}
                    alt={book.title}
                    className="book-cover-image"
                    loading="lazy"
                  />
                  <div className="book-info-overlay">
                    <h3 className="book-title-overlay">{book.title}</h3>
                    <p className="book-author-overlay">{book.author}</p>
                    <span className="book-category-tag">
                      {t.categories.names[book.category] || book.category}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="book-mycraftz-cta"
                    onClick={(e) => { e.stopPropagation(); setInstallBook(book); }}
                    data-testid={`book-mycraftz-${book.id}`}
                    aria-label={`Install an idea from ${book.title} via MyCraftz`}
                  >
                    {(t.mycraftzInstall && t.mycraftzInstall.buttonLabel) || 'MyCraftz™'}
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Categories section — REMOVED per ICRAFTZ 2.0 Phase 1 IA cleanup.
          The 4 reading journeys (Aarohan/Nirmaan/Nava Aarambh/Tattva) are now
          the sole primary discovery system; topic-based browsing was a
          competing pattern that fragmented user intent. The category data and
          underlying books are preserved — only the homepage block is removed. */}

      {/* Curated Collections Section */}
      <section id="curated-collections" className="curated-collections-section">
        <div className="curated-collections-container">
          <motion.div
            className="curated-collections-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="library-title">{accentLastWord("Curated Collections")}</h2>
            <p className="library-subtitle">
              Two books. One powerful idea. Ready in your language.
            </p>
          </motion.div>

          <div className="curated-collections-grid scroll-grid">
            {[
              { id: 1, slug: 'habits_compounding', icon: 'Leaf',     ribbonColor: '#4F6B3D' },
              { id: 2, slug: 'purpose_wisdom',     icon: 'Compass',  ribbonColor: '#2A3F66' },
              { id: 3, slug: 'mind_money',         icon: 'Coins',    ribbonColor: '#7B5530' },
              { id: 4, slug: 'focus_execution',    icon: 'Target',   ribbonColor: '#3F7A78' },
              { id: 5, slug: 'mindset_action',     icon: 'Activity', ribbonColor: '#6B2C2A' },
            ].map((p, idx) => {
              const data = pairingResources[p.id];
              const Icon = iconMap[p.icon];
              return (
                <motion.div
                  key={p.id}
                  className="scroll-card"
                  data-testid={`scroll-card-${p.id}`}
                  style={{ backgroundImage: `url(/scrolls/${p.slug}.png)`, '--ribbon-color': p.ribbonColor }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: 0.04 * idx }}
                >
                  {/* Solid colored bookmark ribbon — top-right corner */}
                  <span className="scroll-ribbon" aria-hidden="true">
                    {Icon && <Icon strokeWidth={2.4} />}
                  </span>

                  {/* Text overlay on right half of scroll */}
                  <div className="scroll-text">
                    <h3 className="scroll-title">{data.title}</h3>
                    <span className="scroll-divider" aria-hidden="true" />
                    <p className="scroll-book">{data.bookNames[0]}</p>
                    <span className="scroll-plus" aria-hidden="true">+</span>
                    <p className="scroll-book">{data.bookNames[1]}</p>
                    <span className="scroll-divider" aria-hidden="true" />

                    <div className="scroll-actions">
                      <button
                        type="button"
                        className="scroll-stamp scroll-stamp-active"
                        data-testid={`scroll-infographic-${p.id}`}
                        onClick={() => setResourcePicker({ pairingIndex: p.id, type: 'infographic' })}
                      >
                        Infographic
                      </button>
                      {data.audio ? (
                        <button
                          type="button"
                          className="scroll-stamp scroll-stamp-active"
                          data-testid={`scroll-podcast-${p.id}`}
                          onClick={() => setResourcePicker({ pairingIndex: p.id, type: 'audio' })}
                        >
                          Podcast
                        </button>
                      ) : (
                        <span className="scroll-stamp scroll-stamp-soon" aria-disabled="true">
                          Podcast · Soon
                        </span>
                      )}
                    </div>

                    <div className="scroll-langs">
                      <span>EN</span><i>|</i>
                      <span>हि</span><i>|</i>
                      <span>ગુ</span><i>|</i>
                      <span>म</span><i>|</i>
                      <span>ಕ</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Bottom tagline */}
          <div className="scroll-tagline" aria-hidden="false">
            <span className="scroll-tagline-quote scroll-tagline-quote-left">❝</span>
            <span className="scroll-tagline-text">Curated wisdom. Timeless lessons. Meaningful growth.</span>
            <span className="scroll-tagline-quote scroll-tagline-quote-right">❞</span>
          </div>
          <span className="scroll-tagline-divider" aria-hidden="true">
            <span className="scroll-tagline-line" />
            <span className="scroll-tagline-mark">❦</span>
            <span className="scroll-tagline-line" />
          </span>

        </div>
      </section>

      {/* Craft Your Reading Journey — 4 curated paths (Aarohan/Nirmaan/Nava Aarambh/Tattva).
          Moved BELOW Personal Library + Curated Collections per ICRAFTZ 2.0 IA. */}
      <CraftYourReadingJourney />

      {/* Resource Language Picker Modal (Curated Collections) */}
      <AnimatePresence>
        {resourcePicker && (
          <motion.div
            className="resource-picker-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setResourcePicker(null)}
            data-testid="resource-picker-overlay"
          >
            <motion.div
              className="resource-picker-modal"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="resource-picker-close"
                onClick={() => setResourcePicker(null)}
                aria-label="Close"
                data-testid="resource-picker-close"
              >
                <X size={18} />
              </button>
              <p className="resource-picker-eyebrow">
                {resourcePicker.type === 'audio' ? 'Podcast' : 'Infographic'}
              </p>
              <h3 className="resource-picker-title">
                Pick your language
              </h3>
              <p className="resource-picker-subtitle">
                Opens in a new tab.
              </p>
              <div className="resource-picker-grid">
                {pickerLanguages.map((lang) => (
                  <a
                    key={lang.code}
                    href={getResourceUrl(lang.code)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-picker-lang"
                    onClick={(e) => {
                      if (resourcePicker?.type === 'audio') {
                        handleAudioLangPick(e, lang.code);
                      } else if (resourcePicker?.type === 'infographic') {
                        handleInfographicLangPick(e, lang.code);
                      } else {
                        setResourcePicker(null);
                      }
                    }}
                    data-testid={`resource-picker-lang-${lang.code}`}
                  >
                    <span className="resource-picker-glyph">{lang.glyph}</span>
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* In-app Audio Player Modal — Curated Collections */}
      <AnimatePresence>
        {audioPlayer && (() => {
          const pair = pairingResources[audioPlayer.pairingIndex];
          const src = pair?.audio?.[audioPlayer.langCode];
          return (
            <motion.div
              className="audio-player-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setAudioPlayer(null)}
              data-testid="audio-player-overlay"
            >
              <motion.div
                className="audio-player-modal"
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 15, scale: 0.98 }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Background: blurred book covers */}
                <div className="audio-player-bg" aria-hidden="true">
                  <img src={pair.covers[0]} alt="" className="audio-player-bg-img audio-player-bg-img--left" />
                  <img src={pair.covers[1]} alt="" className="audio-player-bg-img audio-player-bg-img--right" />
                  <div className="audio-player-bg-overlay" />
                </div>

                <button
                  className="audio-player-close"
                  onClick={() => setAudioPlayer(null)}
                  aria-label="Close player"
                  data-testid="audio-player-close"
                >
                  <X size={20} />
                </button>

                <div className="audio-player-content">
                  <p className="audio-player-eyebrow">Now Playing — {audioPlayer.langLabel}</p>
                  <h3 className="audio-player-title">{pair.title}</h3>

                  <div className="audio-player-covers">
                    <div className="audio-player-cover-wrap">
                      <img src={pair.covers[0]} alt={pair.bookNames[0]} className="audio-player-cover" />
                      <p className="audio-player-cover-label">{pair.bookNames[0]}</p>
                    </div>
                    <div className="audio-player-plus">+</div>
                    <div className="audio-player-cover-wrap">
                      <img src={pair.covers[1]} alt={pair.bookNames[1]} className="audio-player-cover" />
                      <p className="audio-player-cover-label">{pair.bookNames[1]}</p>
                    </div>
                  </div>

                  <audio
                    key={src}
                    src={src}
                    controls
                    autoPlay
                    preload="auto"
                    className="audio-player-audio"
                    data-testid="audio-player-audio"
                  >
                    Your browser does not support the audio element.
                  </audio>

                  <p className="audio-player-footnote">Less Noise · More Thought</p>
                  <p className="audio-player-credit">
                    Original educational interpretation by iCraftz™. Book titles, covers, and quoted passages are property of their respective authors and publishers. Not affiliated with or endorsed by any publisher.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* In-app Infographic Viewer Modal — Curated Collections */}
      <AnimatePresence>
        {infographicViewer && (() => {
          const pair = pairingResources[infographicViewer.pairingIndex];
          const src = pair?.infographic?.[infographicViewer.langCode];
          return (
            <motion.div
              className="infographic-viewer-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setInfographicViewer(null)}
              data-testid="infographic-viewer-overlay"
            >
              <motion.div
                className="infographic-viewer-modal"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="infographic-viewer-close"
                  onClick={() => setInfographicViewer(null)}
                  aria-label="Close infographic"
                  data-testid="infographic-viewer-close"
                >
                  <X size={20} />
                </button>
                <div className="infographic-viewer-header">
                  <p className="infographic-viewer-eyebrow">Infographic — {infographicViewer.langLabel}</p>
                  <h3 className="infographic-viewer-title">{pair.title}</h3>
                </div>
                <div className="infographic-viewer-image-wrap">
                  <img
                    src={src}
                    alt={`${pair.title} — ${infographicViewer.langLabel} infographic`}
                    className="infographic-viewer-image"
                    data-testid="infographic-viewer-image"
                  />
                </div>
                <p className="infographic-viewer-hint">Pinch or scroll to zoom. Tap outside to close.</p>
                <p className="infographic-viewer-credit">
                  Original educational interpretation by iCraftz™. Any direct quotes are attributed to their authors. Book titles and covers are property of their respective publishers.
                </p>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* In-app Book Preview Video Modal */}
      <AnimatePresence>
        {videoPreview && (
          <motion.div
            className="video-preview-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setVideoPreview(null)}
            data-testid="video-preview-overlay"
          >
            <motion.div
              className="video-preview-modal"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="video-preview-close"
                onClick={() => setVideoPreview(null)}
                aria-label="Close preview"
                data-testid="video-preview-close"
              >
                <X size={20} />
              </button>
              <div className="video-preview-header">
                <p className="video-preview-eyebrow">60-sec Preview</p>
                <h3 className="video-preview-title">{videoPreview.title}</h3>
                <p className="video-preview-subtitle">by {videoPreview.author}</p>
              </div>
              <div className="video-preview-wrap">
                <video
                  key={videoPreview.videoSrc}
                  src={videoPreview.videoSrc}
                  controls
                  autoPlay
                  playsInline
                  className="video-preview-video"
                  data-testid="video-preview-video"
                >
                  Your browser does not support video playback.
                </video>
              </div>
              <p className="video-preview-credit">
                AI-generated visual interpretation by iCraftz™. For illustrative preview only. Original work by the respective author and publisher.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MyCraftz™ — Transformation Engine premium preview (moved up in narrative order) */}
      <MyCraftzJourneyTeaser />

      {/* Book Whispers — Your Turn */}
      <BookWhispersSection
        activeLang={activeLang}
        backendUrl={process.env.REACT_APP_BACKEND_URL || ''}
      />

      {/* Chapters Within — Your Story */}
      <ChaptersWithinSection
        activeLang={activeLang}
        backendUrl={process.env.REACT_APP_BACKEND_URL || ''}
      />

      {/* Closing Om ribbon — master design system */}
      <ClosingOmRibbon />

      {/* Footer - Vintage */}
      <LandingFooter t={t} />

      {/* MyCraftz™ Install Overlay — 6-step funnel triggered from any book card */}
      <MyCraftzInstallOverlay
        open={Boolean(installBook)}
        book={installBook}
        t={t}
        onClose={() => setInstallBook(null)}
      />

      {/* NEW: 2x2 Quadrant Book Modal */}
      <AnimatePresence>
        {selectedBook && (
          <motion.div
            className="quadrant-modal-overlay"
            data-testid="book-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedBook(null)}
          >
            <motion.div
              className="quadrant-modal"
              data-testid="book-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button 
                className="quadrant-modal-close" 
                onClick={() => setSelectedBook(null)}
                data-testid="modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Book Header */}
              <div className="quadrant-modal-header">
                <img 
                  src={selectedBook.image} 
                  alt={selectedBook.title}
                  className="quadrant-book-thumbnail"
                />
                <div className="quadrant-book-info">
                  <h2 className="quadrant-book-title">{selectedBook.title}</h2>
                  <p className="quadrant-book-author">by {selectedBook.author}</p>
                  <span className="quadrant-book-category">
                    {t.categories.names[selectedBook.category] || selectedBook.category}
                  </span>
                </div>
              </div>

              {/* Translation Loading Indicator */}
              {isTranslating && (
                <div className="translation-loading">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t.modal.translating}</span>
                </div>
              )}

              {/* 2x2 Quadrant Grid */}
              <div className="quadrant-grid">
                {/* Summary Quadrant */}
                <motion.div 
                  className="quadrant quadrant-summary"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                >
                  <div className="quadrant-header">
                    <BookOpen className="w-5 h-5" />
                    <h3>{t.modal.labels.summary}</h3>
                  </div>
                  <p className="quadrant-content">
                    {isTranslating ? (
                      <span className="shimmer-text">{selectedBook.summary}</span>
                    ) : (
                      getBookContent(selectedBook).summary
                    )}
                  </p>
                </motion.div>

                {/* Key Insights Quadrant */}
                <motion.div 
                  className="quadrant quadrant-insights"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="quadrant-header">
                    <Lightbulb className="w-5 h-5" />
                    <h3>{t.modal.labels.insights}</h3>
                  </div>
                  <ul className="quadrant-list">
                    {(isTranslating ? selectedBook.insights : getBookContent(selectedBook).insights).map((insight, idx) => (
                      <li key={`${selectedBook?.title || 'book'}-insight-${idx}`} className={isTranslating ? 'shimmer-text' : ''}>{insight}</li>
                    ))}
                  </ul>
                </motion.div>

                {/* Action Items Quadrant */}
                <motion.div 
                  className="quadrant quadrant-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <div className="quadrant-header">
                    <CheckCircle2 className="w-5 h-5" />
                    <h3>{t.modal.labels.actionItems}</h3>
                  </div>
                  <ul className="quadrant-list quadrant-actions-list">
                    {(isTranslating ? selectedBook.actionItems : getBookContent(selectedBook).actionItems).map((action, idx) => (
                      <li key={`${selectedBook?.title || 'book'}-action-${idx}`} className={isTranslating ? 'shimmer-text' : ''}>
                        <span className="action-number">{idx + 1}</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Core Theme Quadrant */}
                <motion.div 
                  className="quadrant quadrant-theme"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="quadrant-header">
                    <Target className="w-5 h-5" />
                    <h3>{t.modal.labels.coreTheme}</h3>
                  </div>
                  <p className={`quadrant-theme-text ${isTranslating ? 'shimmer-text' : ''}`}>
                    {isTranslating ? selectedBook.coreTheme : getBookContent(selectedBook).coreTheme}
                  </p>
                </motion.div>
              </div>

              {/* IF YOU REMEMBER ONE THING — only for the 5 MyCraftz showcase books */}
              {(() => {
                const sc = getShowcaseFor(selectedBook);
                if (!sc) return null;
                return (
                  <motion.div
                    className="mc-remember-block"
                    data-testid="mc-remember-block"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <p className="mc-remember-eyebrow">
                      <Sparkles size={12} /> If you remember one thing…
                    </p>
                    <blockquote className="mc-remember-quote">
                      {sc.rememberOneThing}
                    </blockquote>
                    <div className="mc-remember-foot">
                      <p className="mc-remember-meta">
                        The installable idea: <strong>{sc.ideaName}</strong>
                      </p>
                      <a
                        href={`/mycraftz/journey/${sc.slug}`}
                        className="mc-install-cta"
                        data-testid={`mc-install-cta-${sc.slug}`}
                        onClick={(e) => {
                          e.preventDefault();
                          window.location.href = `/mycraftz/journey/${sc.slug}`;
                        }}
                      >
                        Install Into MyCraftz<span className="mc-tm">™</span> <ArrowRight size={12} />
                      </a>
                    </div>
                  </motion.div>
                );
              })()}

              {/* Buy + Vernacular Coming Soon block */}
              <div className="modal-buy-block" data-testid="modal-buy-block">
                <p className="modal-buy-tagline">
                  Explore the insights. Apply them today.<br />
                  Then buy the full book — only on iCraftz.
                </p>

                <button
                  type="button"
                  className="modal-buy-btn"
                  data-testid="modal-buy-btn"
                  title="We are working directly with authors and publishers to bring you the full book to buy — in your language, only on iCraftz."
                  onClick={() => setNotifyOpen((v) => !v)}
                >
                  Buy the Full Book on iCraftz in Your Preferred Language — Coming Soon
                </button>

                <div className="modal-vernacular-banner" data-testid="modal-vernacular-banner">
                  <div className="mvb-top">
                    <span className="mvb-icon" aria-hidden="true">
                      <BookOpen size={16} />
                    </span>
                    <span className="mvb-title">Buy the Full Book in Your Preferred Language</span>
                    <span className="mvb-pill">Coming Soon</span>
                  </div>
                  <p className="mvb-body">
                    We are partnering directly with authors and publishers to bring you the
                    complete book — not a summary — exclusively on iCraftz, in your language,
                    at a price every Indian can afford.
                  </p>
                  <div className="mvb-bottom">
                    <button
                      type="button"
                      className="mvb-link"
                      onClick={() => setNotifyOpen((v) => !v)}
                      data-testid="modal-notify-toggle"
                    >
                      {notifyOpen ? 'Close' : 'Notify me when it\u2019s ready'} <ArrowRight size={12} />
                    </button>
                    <span className="mvb-tag">Full book — exclusive to iCraftz</span>
                  </div>

                  <AnimatePresence initial={false}>
                    {notifyOpen && (
                      <motion.div
                        key="notify-form"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="mvb-form-wrap"
                      >
                        {notifyStatus === 'success' ? (
                          <div className="mvb-success" data-testid="modal-notify-success">
                            {notifyMessage} <span aria-hidden="true">🎉</span>
                          </div>
                        ) : (
                          <form className="mvb-form" onSubmit={handleNotifySubmit}>
                            <input
                              type="email"
                              className="mvb-input"
                              placeholder="your@email.com"
                              value={notifyEmail}
                              onChange={(e) => setNotifyEmail(e.target.value)}
                              required
                              data-testid="modal-notify-email"
                            />
                            <select
                              className="mvb-select"
                              value={notifyLang}
                              onChange={(e) => setNotifyLang(e.target.value)}
                              data-testid="modal-notify-language"
                            >
                              {NOTIFY_LANGS.map((l) => (
                                <option key={l.code} value={l.code}>{l.label}</option>
                              ))}
                            </select>
                            <button
                              type="submit"
                              className="mvb-submit"
                              disabled={notifySubmitting}
                              data-testid="modal-notify-submit"
                            >
                              {notifySubmitting ? <Loader2 size={14} className="animate-spin" /> : 'Notify Me'}
                            </button>
                          </form>
                        )}
                        {notifyStatus === 'error' && (
                          <p className="mvb-error" data-testid="modal-notify-error">{notifyMessage}</p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* NEW: Category Filter Drawer/Modal */}
      <AnimatePresence>
        {selectedCategory && (
          <>
            {/* Desktop Modal */}
            <motion.div
              className="category-drawer-overlay desktop-only"
              data-testid="category-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedCategory(null)}
            >
              <motion.div
                className="category-modal"
                data-testid="category-modal"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button 
                  className="category-modal-close"
                  onClick={() => setSelectedCategory(null)}
                  data-testid="category-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="category-modal-header">
                  {iconMap[selectedCategory.icon] && 
                    React.createElement(iconMap[selectedCategory.icon], { className: "w-8 h-8" })
                  }
                  <h2>{t.categories.names[selectedCategory.name] || selectedCategory.name}</h2>
                  <p>{selectedCategory.description}</p>
                </div>

                <div className="category-books-scroll">
                  {getCategoryBooks(selectedCategory.name).length > 0 ? (
                    getCategoryBooks(selectedCategory.name).map((book, idx) => (
                      <motion.div
                        key={book.id}
                        className="category-book-card"
                        data-testid={`category-book-${book.id}`}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        onClick={() => {
                          setSelectedCategory(null);
                          setSelectedBook(book);
                        }}
                      >
                        <img src={book.image} alt={book.title} />
                        <h4>{book.title}</h4>
                        <p>{book.author}</p>
                      </motion.div>
                    ))
                  ) : (
                    <div className="category-empty">
                      <BookOpen className="w-12 h-12" />
                      <p>{t.categories.comingSoon}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>

            {/* Mobile Drawer */}
            <motion.div
              className="category-drawer-mobile mobile-only"
              data-testid="category-drawer-mobile"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="drawer-handle" onClick={() => setSelectedCategory(null)} />
              
              <div className="drawer-header">
                {iconMap[selectedCategory.icon] && 
                  React.createElement(iconMap[selectedCategory.icon], { className: "w-6 h-6" })
                }
                <h3>{t.categories.names[selectedCategory.name] || selectedCategory.name}</h3>
                <button onClick={() => setSelectedCategory(null)}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="drawer-description">{selectedCategory.description}</p>

              <div className="drawer-books-scroll">
                {getCategoryBooks(selectedCategory.name).length > 0 ? (
                  getCategoryBooks(selectedCategory.name).map((book, idx) => (
                    <motion.div
                      key={book.id}
                      className="drawer-book-card"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={() => {
                        setSelectedCategory(null);
                        setSelectedBook(book);
                      }}
                    >
                      <img src={book.image} alt={book.title} />
                      <h4>{book.title}</h4>
                    </motion.div>
                  ))
                ) : (
                  <div className="drawer-empty">
                    <BookOpen className="w-10 h-10" />
                    <p>{t.categories.comingSoon}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Mobile backdrop */}
            <motion.div
              className="mobile-backdrop mobile-only"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedCategory(null)}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
